// src/components/AlertNotesConfig.jsx
import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';
import { AlertSeverity } from '../types/schema';

const AlertNotesConfig = ({ notes, onChange }) => {
    const [newNote, setNewNote] = useState({ text: '', severity: AlertSeverity.INFO });

    const handleAddNote = () => {
        if (!newNote.text.trim()) return;

        const noteToAdd = {
            id: uuidv4(),
            text: newNote.text,
            severity: newNote.severity
        };

        onChange([...notes, noteToAdd]);
        setNewNote({ text: '', severity: AlertSeverity.INFO });
    };

    const handleDeleteNote = (id) => {
        onChange(notes.filter(note => note.id !== id));
    };

    const handleSeverityChange = (event) => {
        setNewNote(prev => ({ ...prev, severity: event.target.value }));
    };

    return (
        <Box>
            <Typography variant="subtitle1" gutterBottom>
                Alert / Help Notes
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Note Text"
                            value={newNote.text}
                            onChange={(e) => setNewNote(prev => ({ ...prev, text: e.target.value }))}
                            multiline
                            rows={2}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Severity</InputLabel>
                            <Select
                                value={newNote.severity}
                                onChange={handleSeverityChange}
                                label="Severity"
                            >
                                <MenuItem value={AlertSeverity.INFO}>Info</MenuItem>
                                <MenuItem value={AlertSeverity.WARNING}>Warning</MenuItem>
                                <MenuItem value={AlertSeverity.ERROR}>Error</MenuItem>
                                <MenuItem value={AlertSeverity.SUCCESS}>Success</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddNote}
                        >
                            Add
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {notes.length > 0 && (
                <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Preview:
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: `${newNote.severity}.light`,
                            border: 1,
                            borderColor: `${newNote.severity}.main`
                        }}
                    >
                        {notes.map((note) => (
                            <Box key={note.id} display="flex" alignItems="center" mb={1}>
                                <Chip
                                    label={note.severity}
                                    size="small"
                                    sx={{ mr: 1 }}
                                    color={note.severity}
                                />
                                <Typography variant="body2">
                                    {note.text}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => handleDeleteNote(note.id)}
                                    sx={{ ml: 'auto' }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Paper>
                </Box>
            )}

            {notes.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No alert notes added. Users will see no help text for this field.
                </Typography>
            )}
        </Box>
    );
};

export default AlertNotesConfig;